// Deterministic PRNG for fixture generation. Mulberry32 algorithm.
// Same seed in always produces same sequence out. Re-runs of the build produce identical fixtures.

export function makePrng(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function pickFrom<T>(rng: () => number, arr: readonly T[]): T {
  if (arr.length === 0) throw new Error('pickFrom: empty array');
  const idx = Math.floor(rng() * arr.length);
  return arr[idx]!;
}

export function pickWeighted<T>(
  rng: () => number,
  options: ReadonlyArray<readonly [T, number]>,
): T {
  let total = 0;
  for (const [, weight] of options) total += weight;
  const r = rng() * total;
  let cursor = 0;
  for (const [value, weight] of options) {
    cursor += weight;
    if (r <= cursor) return value;
  }
  return options[options.length - 1]![0];
}

export function randRange(rng: () => number, min: number, max: number): number {
  return Math.floor(rng() * (max - min + 1)) + min;
}

/**
 * A stable 32-bit hash of a string (FNV-1a). Same string in always gives the
 * same number out, in any run or any tab, so it can seed `makePrng` or stand in
 * for a random id suffix where the id has to survive a replay unchanged.
 */
export function stableHash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}
